/**
 * Email Audit Trail Component
 * Tracks and displays all emails sent by the system
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Replay as ResendIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost } from '../../utils/apiClient';
import logger from '../../utils/logger';
import SortableHeaderCell from '../common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';
import usePagination from '../../hooks/usePagination';
import { useNotification } from '../../contexts/NotificationContext';

function EmailAuditTrail() {
  const notify = useNotification();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  const pagination = usePagination({ defaultRowsPerPage: 25 });
  const { page, rowsPerPage, resetPage } = pagination;
  const [totalCount, setTotalCount] = useState(0);
  const sort = useTableSort({ onSortChange: resetPage });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterRecipient, setFilterRecipient] = useState('');

  // Notification types fetched from the API registry (single source of truth) so the
  // Type filter + labels never drift from the backend.
  const [notificationTypes, setNotificationTypes] = useState([]);

  useEffect(() => {
    loadEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEmails is also called from filter/resend handlers
  }, [page, rowsPerPage, sort.apiParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet('/email/notification-types');
        if (res.success && Array.isArray(res.data)) setNotificationTypes(res.data);
      } catch (err) {
        logger.error('Error loading notification types', { message: err.message });
      }
    })();
  }, []);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterType !== 'all' && { notification_type: filterType }),
        ...(filterDateFrom && { date_from: filterDateFrom }),
        ...(filterDateTo && { date_to: filterDateTo }),
        ...(filterRecipient && { recipient: filterRecipient }),
        ...sort.apiParams,
      });

      // Use the new email logs API endpoint
      const response = await apiGet(`/email/logs?${params}`);
      if (response.success && response.data) {
        setEmails(response.data);
        setTotalCount(response.pagination?.total || response.data.length);
      } else {
        // No data available
        setEmails([]);
        setTotalCount(0);
      }
    } catch (err) {
      logger.error('Error loading emails', { message: err.message });
      // Show empty state on error
      setEmails([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (emailId) => {
    try {
      setResending(emailId);
      const response = await apiPost(`/email/resend/${emailId}`);
      if (response.success) {
        notify.success('Email resent successfully');
        // Reload the list to show updated status
        loadEmails();
      } else {
        throw new Error(response.message || 'Failed to resend email');
      }
    } catch (err) {
      logger.error('Error resending email', { message: err.message });
      notify.error(err.message || 'Failed to resend email');
    } finally {
      setResending(null);
    }
  };

  const handleViewEmail = async (email) => {
    // Show the row immediately; the list endpoint doesn't return the body, so fetch
    // the full record (incl. body) from GET /logs/:id and merge it in.
    setSelectedEmail(email);
    setViewDialogOpen(true);
    try {
      const res = await apiGet(`/email/logs/${email.id}`);
      if (res.success && res.data) {
        setSelectedEmail(prev => (prev && prev.id === email.id ? { ...prev, ...res.data } : prev));
      }
    } catch (err) {
      logger.error('Error loading email detail', { message: err.message });
    }
  };

  const handleApplyFilters = () => {
    resetPage();
    loadEmails();
  };

  const handleResetFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterRecipient('');
    resetPage();
    loadEmails();
  };

  // email_log.status is one of pending | sent | failed (DB CHECK constraint).
  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <SuccessIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Resolve a notification type's friendly label from the fetched registry.
  const getEmailTypeName = (type) => {
    const found = notificationTypes.find(t => t.key === type);
    return found ? found.label : type;
  };

  // Render a log row's timestamp: failed rows have no sent_at, so fall back to created_at.
  const formatLogDate = (email, fmt = 'dd MMM yyyy HH:mm') => {
    const ts = email?.sent_at || email?.created_at;
    return ts ? format(new Date(ts), fmt) : '-';
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Email Audit Trail
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Track all emails the system has attempted to send, with their delivery status (sent / failed) and any error.
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Typography>Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    md: 3
                  }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="sent">Sent</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 3
                  }}>
                  <FormControl fullWidth>
                    <InputLabel>Email Type</InputLabel>
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      label="Email Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {notificationTypes.map((t) => (
                        <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <TextField
                    fullWidth
                    label="Date From"
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <TextField
                    fullWidth
                    label="Date To"
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <TextField
                    fullWidth
                    label="Recipient Email"
                    value={filterRecipient}
                    onChange={(e) => setFilterRecipient(e.target.value)}
                    placeholder="email@example.com"
                  />
                </Grid>

                <Grid size={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleResetFilters}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleApplyFilters}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Email History ({totalCount} total)
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadEmails}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell sort={sort} columnKey="created_at">Date Sent</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="recipient_email">Recipient</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="subject">Subject</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="notification_type">Type</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="status">Status</SortableHeaderCell>
                <TableCell>Reference</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emails.map((email) => (
                <TableRow key={email.id} hover>
                  <TableCell>
                    {formatLogDate(email)}
                  </TableCell>
                  <TableCell>{email.recipient_email}</TableCell>
                  <TableCell>{email.subject}</TableCell>
                  <TableCell>
                    <Chip
                      label={getEmailTypeName(email.notification_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(email.status)}
                      label={email.status}
                      color={getStatusColor(email.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {email.reference_type
                      ? `${email.reference_type}${email.reference_id ? ` #${email.reference_id}` : ''}`
                      : (email.reference_id || '-')}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View email details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewEmail(email)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(email.status === 'failed' || email.status === 'bounced') && (
                      <Tooltip title="Resend email">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleResendEmail(email.id)}
                          disabled={resending === email.id}
                        >
                          <ResendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination {...pagination.tableProps(totalCount)} />
      </Paper>
      {/* View Email Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Details
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Recipient</Typography>
                  <Typography variant="body1">
                    {selectedEmail.recipient_name
                      ? `${selectedEmail.recipient_name} <${selectedEmail.recipient_email}>`
                      : selectedEmail.recipient_email}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Subject</Typography>
                  <Typography variant="body1">{selectedEmail.subject}</Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Email Type</Typography>
                  <Typography variant="body1">{getEmailTypeName(selectedEmail.notification_type)}</Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedEmail.status)}
                    label={selectedEmail.status}
                    color={getStatusColor(selectedEmail.status)}
                    size="small"
                  />
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Sent At</Typography>
                  <Typography variant="body1">
                    {formatLogDate(selectedEmail, 'dd MMM yyyy HH:mm:ss')}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Reference</Typography>
                  <Typography variant="body1">
                    {selectedEmail.reference_type
                      ? `${selectedEmail.reference_type}${selectedEmail.reference_id ? ` #${selectedEmail.reference_id}` : ''}`
                      : (selectedEmail.reference_id || 'N/A')}
                  </Typography>
                </Grid>

                {selectedEmail.error_message && (
                  <Grid size={12}>
                    <Typography variant="caption" color="textSecondary">Error</Typography>
                    <Typography variant="body2" color="error.main">{selectedEmail.error_message}</Typography>
                  </Grid>
                )}

                <Grid size={12}>
                  <Typography variant="caption" color="textSecondary">Email Body</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50', maxHeight: 360, overflow: 'auto' }}>
                    {selectedEmail.body
                      ? (/<[a-z][\s\S]*>/i.test(selectedEmail.body)
                          ? <Box sx={{ '& *': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: selectedEmail.body }} />
                          : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedEmail.body}</Typography>)
                      : <Typography variant="body2" color="textSecondary">Loading…</Typography>}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailAuditTrail;
